import {
  AfterViewInit, Component, ElementRef, HostListener, NgZone,
  OnDestroy, OnInit, QueryList, ViewChild, ViewChildren
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import { ApiService } from '../../core/services/api.service';
import { PlayQueueService, PlayQueueItem } from '../../core/services/play-queue.service';
import { UserService } from '../../core/services/user.service';
import { VideoShareService } from '../../core/services/video-share.service';
import { environment } from '../../../environments/environment';

/** hls.js is pulled in globally through angular.json "scripts", same as the audio
 * player — hence the global declaration rather than an ES import. */
declare var Hls: any;

/** Wheel input: accumulated pixels that count as one deliberate gesture. A mouse notch
 * clears it in a single event, a trackpad flick in a handful; a hesitant nudge never does. */
const WHEEL_TRIGGER_PX = 40;
/** A wheel gesture is over once no wheel event has arrived for this long. A trackpad keeps
 * emitting momentum events for a second or more after the fingers lift, with gaps well
 * under this — so the whole flick, tail included, is treated as one gesture. */
const WHEEL_QUIET_MS = 200;
/** The feed counts as settled once it has not scrolled for this long. */
const SCROLL_SETTLE_MS = 120;

/**
 * Full-screen vertical shorts feed (YouTube Shorts / Instagram Reels style).
 *
 * Deliberately separate from DrmPlayerComponent: shorts need a swipeable feed of many
 * videos rather than one player with chrome, and they are plain HLS — no DRM/EME, no
 * control bar, no up-next overlays. Playback runs on native <video> + hls.js so each
 * slide stays a plain DOM element that Angular owns (videojs would rewrite it).
 *
 * Only a 3-slide window (active ± 1) ever holds a decoder; everything else is torn
 * down, so a 50-short feed costs the same memory as three videos.
 */
@Component({
  selector: 'app-shorts-player',
  templateUrl: './shorts-player.component.html',
  styleUrls: ['./shorts-player.component.scss']
})
export class ShortsPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('stageEl') stageEl: ElementRef<HTMLElement>;
  @ViewChild('feedEl') feedEl: ElementRef<HTMLElement>;
  @ViewChildren('slideEl') slideEls: QueryList<ElementRef<HTMLElement>>;

  feed: any[] = [];
  activeIndex = 0;
  loading = true;
  notFound = false;

  /** Start with sound on. Arriving here from a tap on a shorts card carries that
   * gesture's user activation into this route (same document, SPA navigation), so the
   * browser normally allows it. On a cold deep link or reload it can still refuse —
   * playAt() catches that rejection and falls back to muted rather than freezing. */
  muted = false;
  paused = false;
  /** 0-100, drives the thin progress bar under the active short. */
  progress = 0;

  /** Settings popup — same gear menu the normal player shows, rebuilt as template
   * markup on the shared .vjs-settings-* styles because there is no videojs here. */
  settingsOpen = false;
  settingsView: 'main' | 'quality' | 'speed' | 'audio' | 'subtitle' = 'main';
  /** Kept across slides: every attach()/playAt() re-applies it to the new <video>. */
  playbackRate = 1;
  readonly speedOptions = [0.5, 1, 1.25, 1.5, 2];
  /** Same global auto_play preference the normal player's menu toggles. */
  autoPlayEnabled = localStorage.getItem('autoPlay') ? JSON.parse(localStorage.getItem('autoPlay')) : false;

  readonly defaultPoster = 'https://dyo1wwfvu9av7.cloudfront.net/jkp/common/default-poster.png';

  /** PallyCon endpoints — the same three DrmPlayerComponent hands videojs-contrib-eme.
   * Keep them in step with that component: a short and the full video behind it are
   * packaged by the same pipeline and licensed by the same site id. */
  private readonly drmLicenseUri = 'https://license-global.pallycon.com/ri/licenseManager.do';
  private readonly fairplayCertUri = 'https://drm-license.doverunner.com/ri/fpsKeyManager.do?siteId=HLEH';

  /** What is currently bound to a slide, keyed by feed index — only the active ± 1
   * window is populated. The <video> is held directly (not looked up on demand) so
   * teardown still works if the element ever leaves the view. */
  private attachedByIndex = new Map<number, { hls: any, video: HTMLVideoElement }>();
  private observer: IntersectionObserver = null;
  private slidesSub: any = null;
  private routeSub: any = null;
  /** Wheel gesture state: pixels accumulated so far, whether this gesture has already
   * moved the feed, and the timer that declares the gesture over. */
  private wheelAccum = 0;
  private wheelConsumed = false;
  private wheelQuietTimer: any = null;
  private touchStartY = 0;
  /** Index a programmatic smooth-scroll is heading for, or null when settled. Input is
   * ignored while it is set; it clears when the feed stops scrolling (see settleScroll). */
  private pendingIndex: number = null;
  /** Safety net that releases pendingIndex if no scroll event ever follows. */
  private pendingTimer: any = null;
  private scrollSettleTimer: any = null;
  /** Feed element the scroll listener is currently bound to (it is re-created by *ngIf). */
  private scrollBoundEl: HTMLElement = null;
  private readonly onFeedScrollBound = () => this.onFeedScroll();
  /** True when the browser forced us to mute, as opposed to the user asking for it —
   * the difference decides whether the next gesture should turn sound back on. */
  private autoMuted = false;
  /** Slug from the URL — decides which short the feed opens on. */
  private startSlug = '';
  private destroyed = false;

  /** Playlist this short is being played as part of (shorts/:slug/:playlist_id/:name).
   * Empty on the standalone shorts route. */
  playlistId = '';
  playlistName = '';
  /** The playlist entry that follows this feed's last short, and the one before its
   * first — either can be a YouTube embed or a normal video, in which case leaving the
   * feed means switching to the watch player. */
  private queueNext: PlayQueueItem = null;
  private queuePrev: PlayQueueItem = null;

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private videoShareService: VideoShareService,
    private playQueue: PlayQueueService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private zone: NgZone
  ) {}

  /** True while these shorts are one leg of a playlist rather than the endless feed. */
  get inPlaylist(): boolean {
    return !!this.playlistId;
  }

  ngOnInit() {
    this.routeSub = this.route.params.subscribe(params => {
      const slug = params['video_id'];
      const playlistId = params['playlist_id'] || '';
      const playlistName = params['name'] || '';
      if (!slug || (slug === this.startSlug && playlistId === this.playlistId)) return;

      const playlistChanged = playlistId !== this.playlistId;
      this.startSlug = slug;
      this.playlistId = playlistId;
      this.playlistName = playlistName;

      // A slug change from outside (e.g. browser back) re-seeks the existing feed
      // instead of refetching it — but only while the feed still represents the same
      // playlist context.
      const known = playlistChanged ? -1 : this.feed.findIndex(v => v.slug === slug);
      if (known !== -1) {
        this.scrollToIndex(known);
      } else if (playlistId) {
        this.loadPlaylistFeed();
      } else {
        this.loadFeed();
      }
    });

    document.body.classList.add('shorts-player-open');
    this.getAutoPlaySetting();
  }

  ngAfterViewInit() {
    // Slides only exist once the feed resolves, so (re)build the observer whenever
    // the rendered list changes.
    this.slidesSub = this.slideEls.changes.subscribe(() => this.observeSlides());
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.routeSub) this.routeSub.unsubscribe();
    if (this.slidesSub) this.slidesSub.unsubscribe();
    if (this.observer) this.observer.disconnect();
    if (this.pendingTimer) clearTimeout(this.pendingTimer);
    if (this.wheelQuietTimer) clearTimeout(this.wheelQuietTimer);
    if (this.scrollSettleTimer) clearTimeout(this.scrollSettleTimer);
    this.unbindFeedScroll();
    this.attachedIndices().forEach(i => this.detach(i));
    this.attachedByIndex.clear();
    document.body.classList.remove('shorts-player-open');
  }

  // ---------------------------------------------------------------- feed loading

  /** Flatten every section of the shorts landing feed into one scrollable list and
   * open on the short named in the URL. */
  private loadFeed() {
    this.loading = true;
    this.notFound = false;

    this.apiService.callGetAPI('v2/shorts_page').subscribe(
      data => {
        const response = data && data['response'];
        const sections = response && response.shorts_content
          ? Object.keys(response.shorts_content).map(k => response.shorts_content[k])
          : [];

        const seen = new Set<string>();
        const all: any[] = [];
        sections.forEach((section: any) => {
          (section && section.data ? section.data : []).forEach((video: any) => {
            if (!video || !video.slug || seen.has(video.slug)) return;
            seen.add(video.slug);
            all.push(video);
          });
        });

        this.feed = this.orderFromStartSlug(all);
        this.loading = false;
        this.notFound = !this.feed.length;

        if (this.feed.length) {
          this.activeIndex = 0;
          // Wait for *ngFor to render the slides before touching the DOM.
          setTimeout(() => {
            this.observeSlides();
            this.syncWindow();
          }, 0);
        }
      },
      error => {
        this.loading = false;
        this.notFound = true;
        this.apiService.failureResponse(error, true);
      });
  }

  /**
   * Feed for a short opened as part of a playlist.
   *
   * Only shorts can be slides, so the feed is the run of shorts that starts at the one
   * in the URL and stops at the first playlist entry that is not a short. That entry
   * becomes `queueNext`: reaching the end of the feed (by swiping or by the last short
   * ending) hands over to the watch player for it, which is what keeps a mixed playlist
   * playing as one sequence.
   *
   * Anything unexpected — no queue, or a short the playlist does not list — falls back
   * to the normal endless feed rather than leaving the user on a blank page.
   */
  private loadPlaylistFeed() {
    this.loading = true;
    this.notFound = false;

    this.playQueue.ensure(this.playlistId, this.playlistName, this.startSlug).subscribe(queue => {
      if (this.destroyed) return;

      const items = queue ? queue.items : [];
      const start = this.playQueue.indexOf(items, this.startSlug);
      if (start === -1) {
        this.playlistId = '';
        this.playlistName = '';
        this.loadFeed();
        return;
      }

      if (queue.playlistName) {
        this.playlistName = queue.playlistName;
      }

      let end = start;
      while (end + 1 < items.length && this.playQueue.isShort(items[end + 1])) {
        end++;
      }

      this.queuePrev = start > 0 ? items[start - 1] : null;
      this.queueNext = end + 1 < items.length ? items[end + 1] : null;

      // Copy the entries: the feed decorates them with __src/__started, which has no
      // business being written back into the stored queue.
      this.feed = items.slice(start, end + 1).map(item => ({ ...item }));
      this.loading = false;
      this.notFound = !this.feed.length;

      if (this.feed.length) {
        this.activeIndex = 0;
        setTimeout(() => {
          this.observeSlides();
          this.syncWindow();
        }, 0);
      }
    });
  }

  /** Put the short from the URL first so the page opens on what the user clicked.
   * If the feed does not contain it (deep link into an unlisted short), synthesise a
   * minimal entry — its source is resolved lazily from v2/watchvideo anyway. */
  private orderFromStartSlug(all: any[]): any[] {
    if (!this.startSlug) return all;

    const index = all.findIndex(v => v.slug === this.startSlug);
    if (index === -1) {
      return [{ slug: this.startSlug, title: '', thumbnail_image: '' }, ...all];
    }
    return [all[index], ...all.slice(0, index), ...all.slice(index + 1)];
  }

  // ------------------------------------------------------------- active tracking

  /** Watch the slides inside the scroller so native scroll-snap, swipes and our own
   * programmatic scrolls all report the active short through one path. */
  private observeSlides() {
    if (!this.feedEl || !this.slideEls || !this.slideEls.length) return;

    this.bindFeedScroll();
    if (this.observer) this.observer.disconnect();

    // IntersectionObserver fires outside Angular; re-enter the zone only when the
    // active short actually changes.
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
        const index = Number((entry.target as HTMLElement).getAttribute('data-index'));
        if (isNaN(index) || index === this.activeIndex) return;
        this.zone.run(() => this.setActive(index));
      });
    }, { root: this.feedEl.nativeElement, threshold: [0.6] });

    this.slideEls.forEach(ref => this.observer.observe(ref.nativeElement));
  }

  private setActive(index: number) {
    if (index === this.activeIndex) return;

    this.activeIndex = index;
    this.paused = false;
    this.progress = 0;
    this.closeSettings();
    this.pauseAllExcept(index);

    // The new short was already a neighbour, so its source is attached — start it now
    // and only then pull the next one into the window.
    this.playAt(index);
    this.syncUrl();
    this.syncWindow();
  }

  /** Keep the URL in step with the visible short so refresh/share land in the right
   * place, without pushing a history entry per swipe. */
  private syncUrl() {
    const item = this.feed[this.activeIndex];
    if (!item || !item.slug) return;
    // Keep the playlist in the URL too, so a refresh or a shared link resumes the
    // sequence instead of dropping into the endless feed.
    this.location.replaceState(
      this.playQueue.pathFor({ slug: item.slug, is_shorts: 1 }, this.playlistId, this.playlistName));
  }

  /** Leave the feed for a playlist entry that is not a short (YouTube embed, normal or
   * DRM video) — the watch player takes it from there and keeps advancing. */
  private goToQueueItem(item: PlayQueueItem) {
    if (!item || !item.slug) return;
    this.router.navigateByUrl(this.playQueue.pathFor(item, this.playlistId, this.playlistName));
  }

  /** Is there anything after the last slide / before the first one? In a playlist that
   * can be the next or previous playlist entry, not just another short. */
  get canGoNext(): boolean {
    return this.activeIndex < this.feed.length - 1 || (this.inPlaylist && !!this.queueNext);
  }

  get canGoPrev(): boolean {
    return this.activeIndex > 0 || (this.inPlaylist && !!this.queuePrev);
  }

  /** A short ends. On the endless feed it loops (the `loop` attribute); inside a
   * playlist it moves on — next short, or out to whatever the playlist has next. */
  onEnded(index: number) {
    if (index !== this.activeIndex || !this.inPlaylist) return;
    if (index + 1 < this.feed.length) {
      this.scrollToIndex(index + 1);
      return;
    }
    this.goToQueueItem(this.queueNext);
  }

  // ----------------------------------------------------------- source management

  /** Attach the active short and its neighbours; tear down everything else. */
  private syncWindow() {
    const wanted = [this.activeIndex - 1, this.activeIndex, this.activeIndex + 1]
      .filter(i => i >= 0 && i < this.feed.length);

    this.attachedIndices().forEach(i => {
      if (wanted.indexOf(i) === -1) this.detach(i);
    });

    wanted.forEach(i => this.ensurePlayable(i));
  }

  /** Snapshot of the attached keys — detach() deletes from the map, so iterating it
   * directly while tearing down would skip entries. */
  private attachedIndices(): number[] {
    const indices: number[] = [];
    this.attachedByIndex.forEach((_, i) => indices.push(i));
    return indices;
  }

  /** Belt and braces against two shorts playing at once: anything that is not the
   * active slide gets stopped and rewound. */
  private pauseAllExcept(activeIndex: number) {
    this.attachedByIndex.forEach((entry, i) => {
      if (i === activeIndex || !entry.video) return;
      entry.video.pause();
      entry.video.currentTime = 0;
    });
  }

  /** Resolve through the slide rather than a @ViewChildren of the <video> elements —
   * slides map 1:1 with the feed, so the index always lines up. */
  private videoAt(index: number): HTMLVideoElement {
    if (!this.slideEls) return null;
    const slide = this.slideEls.toArray()[index];
    return slide ? slide.nativeElement.querySelector('video') : null;
  }

  private forEachVideo(fn: (video: HTMLVideoElement) => void) {
    if (!this.slideEls) return;
    this.slideEls.forEach(ref => {
      const video = ref.nativeElement.querySelector('video') as HTMLVideoElement;
      if (video) fn(video);
    });
  }

  /** Resolve a playable URL for the short, then bind it to that slide's <video>. */
  private ensurePlayable(index: number) {
    const item = this.feed[index];
    if (!item || item.__srcLoading) return;

    if (item.__src) {
      this.attach(index, item.__src);
      return;
    }

    // Resolution already ran and produced nothing playable — don't ask again on every
    // window sync. attach()'s own retries are unaffected; those go through __src.
    if (item.__noSource) return;

    // A DRM short has to go through watchvideo even when the listing already carries a
    // URL: its licence token is minted per request there and appears in no feed payload.
    const direct = this.isDrm(item) ? '' : this.directSource(item);
    if (direct) {
      item.__src = direct;
      this.attach(index, direct);
      return;
    }

    // No URL in the listing (deep link into an unlisted short) — fall back to the
    // endpoint the normal player uses. Never let this call be the single point of
    // failure: if it does not come back usable, still try whatever the listing gave us.
    item.__srcLoading = true;
    this.apiService.callGetAPI('v2/watchvideo/' + item.slug).subscribe(
      data => {
        item.__srcLoading = false;
        if (this.destroyed) return;

        const videos = data && data['response'] && data['response'].videos;
        if (!videos) {
          console.warn('[shorts] watchvideo returned no video payload for', item.slug, data);
          this.fallbackToListingSource(index, item, direct);
          return;
        }

        this.adoptDetails(item, videos);

        // Protected shorts carry no hls_playlist_url at all, so they never reach the
        // plain-source path below — they play off drm_hls_url through EME instead.
        if (this.isDrm(videos)) {
          this.attachDrm(index, item, videos);
          return;
        }

        const src = this.directSource(videos) || direct;
        if (!src) {
          item.__noSource = true;
          console.warn('[shorts] no playable source for', item.slug, videos);
          return;
        }

        item.__src = src;
        this.attach(index, src);
      },
      error => {
        item.__srcLoading = false;
        if (this.destroyed) return;
        console.warn('[shorts] watchvideo failed for', item.slug, error);
        this.fallbackToListingSource(index, item, direct);
      });
  }

  /** Carry over what the overlay and the share popup show for a short the listing did
   * not describe (deep link into an unlisted short, playlist entry). Never overwrites
   * what the listing already said. */
  private adoptDetails(item: any, videos: any) {
    if (!item.id) item.id = videos.id;
    item.title = item.title || videos.title;
    item.genre_name = item.genre_name || videos.genre_name;
    item.thumbnail_image = item.thumbnail_image || videos.poster_image || videos.thumbnail_image;
    if (typeof item.description === 'undefined') item.description = videos.description;
    if (typeof item.is_like === 'undefined') item.is_like = videos.is_like;
    if (typeof item.like_count === 'undefined') item.like_count = videos.like_count;
    if (typeof item.is_live === 'undefined') item.is_live = videos.is_live;
  }

  /** Last resort: play the URL the listing gave us, token or not. Better a request the
   * server might reject than a slide that silently never starts. */
  private fallbackToListingSource(index: number, item: any, direct: string) {
    if (!direct) {
      item.__noSource = true;
      return;
    }
    item.__src = direct;
    this.attach(index, direct);
  }

  private directSource(item: any): string {
    if (!item) return '';
    return item.hls_playlist_url || item.hls_url || item.video_url || item.mp4_url || '';
  }

  // --------------------------------------------------------------------- DRM shorts

  /** True for a short the API packages with PallyCon. Such a row has an empty
   * hls_playlist_url — drm_hls_url / drm_dash_url are the only sources it has. */
  private isDrm(item: any): boolean {
    return !!item && String(item.video_type || '').toUpperCase() === 'DRM';
  }

  /**
   * Bind a PallyCon-protected short.
   *
   * The CMAF ladder in drm_hls_url signals its keys inline (#EXT-X-KEY with the
   * Widevine PSSH as a data: URI, plus the PlayReady and FairPlay key formats), so
   * hls.js can run the whole EME handshake itself once it is told which key system to
   * ask for and how to authenticate the licence call. That keeps the feed on plain
   * <video> elements instead of pulling videojs into the scroller.
   *
   * drm_dash_url is deliberately unused: the feed loads no DASH engine, and the HLS
   * ladder is the same content.
   */
  private attachDrm(index: number, item: any, videos: any) {
    const src = videos.drm_hls_url || '';
    const token = videos.drm_token || '';

    // No ladder, or no token to authenticate the licence request with — nothing here
    // can decrypt it, so send the user to the player that can.
    if (!src || !token) {
      this.markDrmBlocked(item, 'watchvideo gave no EME-playable source' +
        ' | hls: ' + (src || '(none)') + ' | token: ' + (token ? 'yes' : 'no'));
      return;
    }

    item.__drm = { type: String(videos.token_type || 'Widevine'), token };
    item.__src = src;
    this.attach(index, src);
  }

  /** A protected short this browser cannot play in the feed. The slide swaps its
   * poster for a hand-off to the normal player, which does DRM through videojs. */
  private markDrmBlocked(item: any, reason: string) {
    if (!item) return;
    item.__noSource = true;
    item.__drmBlocked = true;
    console.warn('[shorts] DRM short not playable in the feed:', item.slug, '|', reason);
  }

  /**
   * hls.js EME configuration for one protected short.
   *
   * PallyCon authenticates every licence request with the per-video token in the
   * `pallycon-customdata-v2` header — the same header DrmPlayerComponent sets. Only
   * the key system the API picked for this device is declared, so hls.js never asks
   * the CDM for one the browser cannot serve.
   */
  private emeConfig(drm: { type: string, token: string }): any {
    const type = String(drm.type || 'Widevine').toLowerCase();
    const config: any = { emeEnabled: true, drmSystems: {} };

    if (type === 'fairplay') {
      config.drmSystems['com.apple.fps'] = {
        licenseUrl: this.drmLicenseUri,
        serverCertificateUrl: this.fairplayCertUri
      };
      // FairPlay is the odd one out at PallyCon: the SPC goes up as a form field and
      // the CKC comes back base64-encoded, so both legs need translating. Same shape
      // as DrmPlayerComponent's getLicense().
      config.licenseXhrSetup = (xhr: XMLHttpRequest, url: string,
                                _keyContext: any, challenge: Uint8Array) => {
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
        xhr.setRequestHeader('pallycon-customdata-v2', drm.token);
        return this.asciiBytes('spc=' + this.base64FromBytes(challenge));
      };
      config.licenseResponseCallback = (xhr: XMLHttpRequest) => {
        return this.bytesFromBase64(this.asciiFromBuffer(xhr.response)).buffer;
      };
      return config;
    }

    if (type === 'playready') {
      config.drmSystems['com.microsoft.playready'] = { licenseUrl: this.drmLicenseUri };
    } else {
      config.drmSystems['com.widevine.alpha'] = { licenseUrl: this.drmLicenseUri };
      // Robustness strings are Widevine's vocabulary — asking PlayReady or FairPlay
      // for them makes requestMediaKeySystemAccess reject outright, hence this branch.
      config.drmSystemOptions = {
        videoRobustness: 'SW_SECURE_DECODE',
        audioRobustness: 'SW_SECURE_CRYPTO'
      };
    }

    // Widevine and PlayReady both take the raw challenge as the body; only the auth
    // header has to be added.
    config.licenseXhrSetup = (xhr: XMLHttpRequest, url: string) => {
      xhr.open('POST', url, true);
      xhr.setRequestHeader('pallycon-customdata-v2', drm.token);
    };
    return config;
  }

  /** base64 <-> bytes for the FairPlay exchange. Hand-rolled rather than taken from
   * CryptoJS so the CDM's bytes survive the round trip untouched. */
  private base64FromBytes(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }

  private bytesFromBase64(text: string): Uint8Array {
    const binary = atob(String(text).trim());
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }

  private asciiBytes(text: string): Uint8Array {
    const bytes = new Uint8Array(text.length);
    for (let i = 0; i < text.length; i += 1) bytes[i] = text.charCodeAt(i) & 0xff;
    return bytes;
  }

  private asciiFromBuffer(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer || new ArrayBuffer(0));
    let text = '';
    for (let i = 0; i < bytes.length; i += 1) text += String.fromCharCode(bytes[i]);
    return text;
  }

  // ------------------------------------------------------- media request token
  //
  // The media API refuses HLS requests — playlist, segments and, above all, the AES
  // key (v2/key?key=...) — unless they carry a "Title" header: an AES-CBC token built
  // from environment.crypto_key/crypto_iv. Without it the key endpoint answers
  // 500 {"message":"Crossdomain access denied"}. Every videojs player installs it
  // through videojs.Hls.xhr.beforeRequest; hls.js needs the same via xhrSetup.
  //
  // WHAT THE PAYLOAD MUST BE — verified against the live endpoint:
  //
  //   plainText          v2/key response
  //   -----------------  --------------------------------------------------
  //   undefined/null/0   200, 16 raw bytes   <- the actual AES-128 key
  //   a stale timestamp  200, 16 raw bytes
  //   a FRESH timestamp  200, 96 ASCII chars <- an encrypted blob, not a key
  //
  // hls.js (like VHS) feeds the response bytes straight into AES decryption, so the
  // 96-char blob turns every fragment into noise and the demuxer reports
  // fragParsingError — audio and video both dead, with no network error anywhere.
  //
  // DrmPlayerComponent declares `plainText` and never assigns it, so the token it
  // sends is built from the literal string below — which is exactly why the normal
  // player plays these streams. Match it rather than "correcting" it: reproducing the
  // rolling-timestamp scheme that VideoCardComponent uses is what broke playback here.
  private readonly tokenPayload = 'vplayed/undefined';
  /** The token is a pure function of two constants, so build it once. */
  private cachedToken = '';

  /** Value for the "Title" header on every media request. */
  private mediaToken(item?: any): string {
    // Live streams are exempt in the videojs players — mirror that. Protected shorts
    // are exempt too: they are served straight from CloudFront, never through the
    // media API, and an unexpected custom header there only invites a CORS preflight.
    if (item && (Number(item.is_live) === 1 || item.__drm)) return '';
    if (this.cachedToken) return this.cachedToken;

    const utf8 = CryptoJS.enc.Utf8.parse(this.tokenPayload);
    const hex = this.binaryToHex(utf8.toString(CryptoJS.enc.Utf8));
    const encrypted = CryptoJS.AES.encrypt(
      hex,
      CryptoJS.enc.Hex.parse(environment.crypto_key),
      {
        keySize: 256,
        iv: CryptoJS.enc.Hex.parse(environment.crypto_iv),
        mode: CryptoJS.mode.CBC
      });

    // NOTE: lowercase `enc.utf8` is not a real CryptoJS encoder, so toString() falls
    // back to Hex. Kept exactly as the other players write it — "fixing" the casing
    // would change the header value and the server would stop accepting it.
    this.cachedToken = encrypted.ciphertext.toString((CryptoJS.enc as any).utf8);
    return this.cachedToken;
  }

  private binaryToHex(num: string): string {
    let str = '';
    for (let i = 0; i < num.length; i += 1) {
      str += num.charCodeAt(i).toString(16);
    }
    return str;
  }

  /** Bind a source to the slide's <video>: hls.js where MSE is needed, native src on
   * Safari and for progressive files. */
  private attach(index: number, src: string, retries = 6) {
    const video = this.videoAt(index);

    // The element may still be a tick away from rendering — retry briefly rather than
    // silently leaving the slide on its poster.
    if (!video) {
      if (retries > 0 && !this.destroyed) {
        setTimeout(() => this.attach(index, src, retries - 1), 80);
      }
      return;
    }

    const existing = this.attachedByIndex.get(index);
    if (existing && existing.video === video && video.getAttribute('data-src') === src) return;

    this.detach(index);
    video.setAttribute('data-src', src);
    video.muted = this.muted;
    video.playbackRate = this.playbackRate;

    // hls.js first, native second — never the other way round: Chrome answers "maybe"
    // to canPlayType('application/vnd.apple.mpegurl') and then plays nothing at all.
    // Hls.isSupported() is the only honest signal here.
    const isHls = /\.m3u8(\?|$)/i.test(src);

    const item = this.feed[index];
    const useHlsJs = isHls && typeof Hls !== 'undefined' && Hls.isSupported();

    if (useHlsJs) {
      // Shorts are a minute or two — a small buffer keeps swiping cheap.
      const hlsConfig: any = {
        maxBufferLength: 20,
        maxMaxBufferLength: 40,
        enableWorker: true,
        // Applies to playlist, fragment AND key requests: hls.js' key loader is built
        // from the same config.loader (XhrLoader), so this is what gets the "Title"
        // header onto v2/key — the request that 500s without it.
        xhrSetup: (xhr: XMLHttpRequest) => {
          const token = this.mediaToken(item);
          if (token) xhr.setRequestHeader('Title', token);
        }
      };
      // Protected shorts additionally need EME: key system, licence URL and the
      // PallyCon auth header.
      if (item && item.__drm) Object.assign(hlsConfig, this.emeConfig(item.__drm));

      const hls = new Hls(hlsConfig);
      this.attachedByIndex.set(index, { hls, video });

      hls.attachMedia(video);
      hls.loadSource(src);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (index === this.activeIndex) this.playAt(index);
      });

      // Bounded, and always logged. Retrying a fatal network error in silence is what
      // turns "the key 500s" into "nothing happens and there is no error".
      let networkRetries = 0;
      hls.on(Hls.Events.ERROR, (_evt: any, data: any) => {
        if (!data) return;

        if (!data.fatal) {
          console.warn('[shorts] hls warning', data.type, data.details,
            '| reason:', data.reason,
            '| err:', data.err && (data.err.message || data.err),
            '| frag:', data.frag && data.frag.url,
            '| http:', data.response && data.response.code);
          return;
        }

        console.error('[shorts] FATAL hls error on', item && item.slug,
          '| type:', data.type, '| details:', data.details,
          '| http:', data.response && data.response.code, '| url:', data.url || '(n/a)');

        if (data.type === Hls.ErrorTypes.KEY_SYSTEM_ERROR) {
          // No CDM, a licence the server refused, or a robustness level this device
          // cannot meet — retrying changes none of that.
          this.markDrmBlocked(item, data.details);
          this.detach(index);
        } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR && networkRetries < 3) {
          networkRetries += 1;
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        } else {
          item.__noSource = true;
          this.detach(index);
        }
      });
    } else {
      // Safari (native HLS) and progressive files. A native <video> src cannot carry
      // the "Title" header, so protected HLS will fail here — same limitation the
      // videojs players work around with overrideNative, which iOS also cannot do.
      if (item && item.__drm) {
        // Native HLS gives us no hook for the PallyCon licence exchange (old iOS
        // Safari, where hls.js has no MSE to work with). The normal player does DRM
        // through videojs, so hand this short over rather than half-starting it.
        this.markDrmBlocked(item, 'native HLS playback cannot run the EME handshake');
        return;
      }
      if (isHls) {
        if (!video.canPlayType('application/vnd.apple.mpegurl')) {
          console.warn('[shorts] no HLS support in this browser for', src);
        } else if (this.mediaToken(item)) {
          console.warn('[shorts] native HLS cannot send the Title header — protected ' +
            'streams will fail on this browser:', src);
        }
      }
      this.attachedByIndex.set(index, { hls: null, video });
      video.src = src;
      video.load();
      if (index === this.activeIndex) this.playAt(index);
    }
  }

  /** Stop and unbind a slide. Goes through the stored <video> reference so teardown
   * works even if the element has since left the view — a detached but still-loaded
   * <video> keeps playing audio that nothing on screen can stop. */
  private detach(index: number) {
    const entry = this.attachedByIndex.get(index);
    this.attachedByIndex.delete(index);

    const video = (entry && entry.video) || this.videoAt(index);
    if (entry && entry.hls) entry.hls.destroy();
    if (!video) return;

    video.pause();
    video.removeAttribute('data-src');
    video.removeAttribute('src');
    video.load();

    const item = this.feed[index];
    if (item) item.__started = false;
  }

  private playAt(index: number) {
    const video = this.videoAt(index);
    if (!video) return;
    video.muted = this.muted;
    video.playbackRate = this.playbackRate;
    const played = video.play();
    if (played && played.catch) {
      played.catch(err => {
        // Autoplay can still be refused (e.g. unmuted after a reload) — fall back to
        // muted playback rather than showing a frozen frame.
        if (!this.muted) {
          console.warn('[shorts] play() rejected, retrying muted:', err && err.name);
          this.muted = true;
          this.autoMuted = true;
          video.muted = true;
          video.play().catch(e2 => {
            console.warn('[shorts] muted play() also rejected:', e2 && e2.name, e2);
            this.paused = true;
          });
          return;
        }
        // Muted playback was blocked too — show the play badge and wait for a tap.
        console.warn('[shorts] muted play() rejected:', err && err.name, err);
        this.paused = true;
      });
    }
  }

  // -------------------------------------------------------------- user gestures

  /**
   * One wheel gesture advances exactly one short.
   *
   * A gesture is the whole stream of wheel events until it goes quiet: a mouse notch is
   * one or two events, a trackpad flick is dozens spread over a second or more as the
   * momentum decays. The first time the accumulated delta crosses the threshold the feed
   * moves; everything else in the gesture is swallowed. Earlier versions released the
   * lock on a fixed timer (or as soon as the next slide was 60% visible), and the tail
   * of the same flick then counted as a second gesture — one swipe, two shorts.
   */
  onWheel(event: WheelEvent) {
    if (this.modalOpen) return;
    // Native wheel scrolling is never allowed here, not even for tiny deltas: with
    // mandatory snapping the browser would snap one further on its own.
    event.preventDefault();

    if (this.wheelQuietTimer) clearTimeout(this.wheelQuietTimer);
    this.wheelQuietTimer = setTimeout(() => this.endWheelGesture(), WHEEL_QUIET_MS);

    // Already moved for this gesture, or it started while a move is still in flight —
    // either way nothing else in it may move the feed again.
    if (this.wheelConsumed) return;
    if (this.pendingIndex !== null) {
      this.wheelConsumed = true;
      return;
    }

    const delta = this.wheelDeltaPx(event);
    if (!delta) return;
    // A direction change starts the count over rather than cancelling against it.
    if ((delta > 0) !== (this.wheelAccum > 0)) this.wheelAccum = 0;
    this.wheelAccum += delta;
    if (Math.abs(this.wheelAccum) < WHEEL_TRIGGER_PX) return;

    const direction = this.wheelAccum > 0 ? 1 : -1;
    this.wheelConsumed = true;
    this.wheelAccum = 0;
    this.recoverAudio();
    this.scrollToIndex(this.activeIndex + direction);
  }

  private endWheelGesture() {
    this.wheelQuietTimer = null;
    this.wheelConsumed = false;
    this.wheelAccum = 0;
  }

  /** deltaY in pixels whatever the deltaMode — Firefox reports mouse wheels in lines. */
  private wheelDeltaPx(event: WheelEvent): number {
    if (event.deltaMode === 1) return event.deltaY * 16;
    if (event.deltaMode === 2) {
      return event.deltaY * (this.feedEl ? this.feedEl.nativeElement.clientHeight : 800);
    }
    return event.deltaY;
  }

  onTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches.length ? event.touches[0].clientY : 0;
    this.recoverAudio();
  }

  /** Touch is left entirely to CSS scroll-snap (mandatory + scroll-snap-stop: always),
   * which already advances one slide per swipe. An earlier version nudged the scroller
   * after a short flick, and when the browser's own snap was still in flight the two
   * added up to a two-slide jump. */
  onTouchEnd(_event: TouchEvent) {
    this.touchStartY = 0;
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (this.modalOpen) return;
    const key = event.key;
    if (key === 'ArrowDown' || key === 'PageDown') {
      event.preventDefault();
      // Key repeat while a move is in flight would stack moves — one per settled scroll.
      if (this.pendingIndex === null) this.scrollToIndex(this.activeIndex + 1);
    } else if (key === 'ArrowUp' || key === 'PageUp') {
      event.preventDefault();
      if (this.pendingIndex === null) this.scrollToIndex(this.activeIndex - 1);
    } else if (key === ' ' || key === 'Spacebar') {
      event.preventDefault();
      this.togglePlay();
    } else if (key === 'm' || key === 'M') {
      this.toggleMute();
    } else if (key === 'Escape' && this.settingsOpen) {
      this.closeSettings();
    }
  }

  scrollToIndex(index: number) {
    // Scrolling off either end of a playlist leg is how the user steps to the next or
    // previous playlist entry, which may well be a YouTube or normal video.
    if (this.inPlaylist && index >= this.feed.length && this.queueNext) {
      this.goToQueueItem(this.queueNext);
      return;
    }
    if (this.inPlaylist && index < 0 && this.queuePrev) {
      this.goToQueueItem(this.queuePrev);
      return;
    }
    if (index < 0 || index >= this.feed.length || !this.slideEls || !this.feedEl) return;
    const ref = this.slideEls.toArray()[index];
    if (!ref) return;

    const feed = this.feedEl.nativeElement;
    const top = ref.nativeElement.offsetTop;
    // Already there: no scroll will follow, so there is nothing to wait for.
    if (Math.abs(feed.scrollTop - top) < 2) return;

    // Remember where the smooth scroll is headed so nothing queues a second move on
    // top of it. settleScroll() releases this once the feed actually stops moving; the
    // timer only covers a browser that never delivers the scroll events.
    this.pendingIndex = index;
    if (this.pendingTimer) clearTimeout(this.pendingTimer);
    this.pendingTimer = setTimeout(() => this.settleScroll(), 1000);

    // Scroll the feed itself rather than scrollIntoView: it cannot drag any ancestor
    // along, and the target is an exact snap position so the snap has nothing to fix.
    // The observer flips activeIndex on the way, so scrollbar drags and this path agree.
    feed.scrollTo({ top, behavior: 'smooth' });
  }

  /** Listen to the feed's scroll events outside Angular — they fire on every frame of a
   * smooth scroll and nothing in the template depends on them. */
  private bindFeedScroll() {
    const el = this.feedEl ? this.feedEl.nativeElement : null;
    if (!el || el === this.scrollBoundEl) return;
    this.unbindFeedScroll();
    this.scrollBoundEl = el;
    this.zone.runOutsideAngular(() => {
      el.addEventListener('scroll', this.onFeedScrollBound, { passive: true });
    });
  }

  private unbindFeedScroll() {
    if (!this.scrollBoundEl) return;
    this.scrollBoundEl.removeEventListener('scroll', this.onFeedScrollBound);
    this.scrollBoundEl = null;
  }

  private onFeedScroll() {
    if (this.scrollSettleTimer) clearTimeout(this.scrollSettleTimer);
    this.scrollSettleTimer = setTimeout(() => this.settleScroll(), SCROLL_SETTLE_MS);
  }

  /** The feed has stopped moving: accept input again, and make sure activeIndex is the
   * slide actually on screen (every slide is exactly one feed height tall). Covers a
   * swipe the IntersectionObserver missed as well as the end of our own smooth scroll. */
  private settleScroll() {
    this.scrollSettleTimer = null;
    if (this.pendingTimer) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    this.pendingIndex = null;

    const feed = this.feedEl ? this.feedEl.nativeElement : null;
    if (!feed || !feed.clientHeight || this.destroyed) return;
    const index = Math.round(feed.scrollTop / feed.clientHeight);
    if (index !== this.activeIndex && index >= 0 && index < this.feed.length) {
      this.zone.run(() => this.setActive(index));
    }
  }

  togglePlay() {
    // A tap while force-muted means "give me sound", not "pause".
    if (this.recoverAudio()) return;

    const video = this.videoAt(this.activeIndex);
    if (!video) return;

    if (video.paused) {
      this.paused = false;
      this.playAt(this.activeIndex);
    } else {
      video.pause();
      this.paused = true;
    }
  }

  toggleMute() {
    this.muted = !this.muted;
    this.autoMuted = false; // from here on the mute state is the user's choice
    this.forEachVideo(video => video.muted = this.muted);
    // Unmuting is a user gesture — a video parked by a blocked autoplay can start now.
    if (!this.muted) this.playAt(this.activeIndex);
  }

  /** The feed wants sound on. If the browser refused at load time, the first real
   * gesture is the moment it will accept — take it. Returns true if it acted, so the
   * gesture that restored audio does not also pause the video.  */
  private recoverAudio(): boolean {
    if (!this.autoMuted) return false;
    this.autoMuted = false;
    this.muted = false;
    this.forEachVideo(video => video.muted = false);
    this.playAt(this.activeIndex);
    return true;
  }

  onTimeUpdate(index: number) {
    if (index !== this.activeIndex) return;
    const video = this.videoAt(index);
    if (!video || !video.duration || isNaN(video.duration)) return;
    this.progress = (video.currentTime / video.duration) * 100;
  }

  /** Scrub by tapping the progress bar. */
  onSeek(event: MouseEvent) {
    const video = this.videoAt(this.activeIndex);
    if (!video || !video.duration || isNaN(video.duration)) return;

    const bar = event.currentTarget as HTMLElement;
    const ratio = (event.clientX - bar.getBoundingClientRect().left) / bar.offsetWidth;
    video.currentTime = Math.max(0, Math.min(1, ratio)) * video.duration;
  }

  // ------------------------------------------------------------- settings menu

  /** hls.js instance behind the active slide, if that slide plays through hls.js
   * (null on native playback — Safari and progressive files). */
  private activeHls(): any {
    const entry = this.attachedByIndex.get(this.activeIndex);
    return entry ? entry.hls : null;
  }

  toggleSettings() {
    this.settingsOpen = !this.settingsOpen;
    this.settingsView = 'main';
  }

  closeSettings() {
    this.settingsOpen = false;
    this.settingsView = 'main';
  }

  openSettingsView(view: 'main' | 'quality' | 'speed' | 'audio' | 'subtitle') {
    this.settingsView = view;
  }

  /** A tap anywhere outside the menu and its gear button dismisses the popup —
   * the videojs menu does the same via its hover/hide timers. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.settingsOpen) return;
    const target = event.target as HTMLElement;
    if (target && target.closest &&
        target.closest('.shorts-settings-menu, .shorts-settings-btn')) return;
    this.closeSettings();
  }

  get qualityLevels(): any[] {
    const hls = this.activeHls();
    return hls && hls.levels ? hls.levels : [];
  }

  get isAutoQuality(): boolean {
    const hls = this.activeHls();
    return !hls || hls.autoLevelEnabled;
  }

  get currentLevelIndex(): number {
    const hls = this.activeHls();
    return hls ? hls.currentLevel : -1;
  }

  /** Resolution actually playing right now — shown next to "Auto", like the
   * normal player's quality menu does. */
  get currentQualityHeight(): number {
    const hls = this.activeHls();
    if (!hls || !hls.levels || hls.currentLevel < 0) return 0;
    const level = hls.levels[hls.currentLevel];
    return level && level.height ? level.height : 0;
  }

  /** -1 selects Auto, anything else pins that hls.js level. */
  setQuality(index: number) {
    const hls = this.activeHls();
    if (hls) hls.currentLevel = index;
    this.closeSettings();
  }

  setSpeed(rate: number) {
    this.playbackRate = rate;
    this.forEachVideo(video => video.playbackRate = rate);
    this.closeSettings();
  }

  get audioTracks(): any[] {
    const hls = this.activeHls();
    return hls && hls.audioTracks ? hls.audioTracks : [];
  }

  get currentAudioTrack(): number {
    const hls = this.activeHls();
    return hls ? hls.audioTrack : -1;
  }

  setAudioTrack(index: number) {
    const hls = this.activeHls();
    if (hls) hls.audioTrack = index;
    this.closeSettings();
  }

  toggleFullscreen() {
    const doc: any = document;
    const isFs = !!(doc.fullscreenElement || doc.webkitFullscreenElement);
    if (isFs) {
      if (doc.exitFullscreen) doc.exitFullscreen();
      else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
    } else {
      const el: any = this.stageEl ? this.stageEl.nativeElement : document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    }
    this.closeSettings();
  }

  togglePip() {
    const doc: any = document;
    const video: any = this.videoAt(this.activeIndex);
    if (doc.pictureInPictureElement) {
      doc.exitPictureInPicture();
    } else if (video && video.requestPictureInPicture) {
      video.requestPictureInPicture().catch((err: any) =>
        console.warn('[shorts] PiP rejected:', err && err.name));
    }
    this.closeSettings();
  }

  /** ⏪ / ⏩ transport row — same ±10s the normal player's menu uses. */
  seekBy(seconds: number) {
    const video = this.videoAt(this.activeIndex);
    if (!video || !video.duration || isNaN(video.duration)) return;
    video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
  }

  /** Same fetch the normal player does: auto_play === 1 on the customer record. */
  private getAutoPlaySetting() {
    this.apiService.callGetAPI('v2/customer/subscription', environment.userApiUrl)
      .subscribe(
        data => {
          this.autoPlayEnabled = !!(data && data['response'] && data['response']['auto_play'] == 1);
        },
        error => {
          console.warn('[shorts] auto_play setting fetch failed; keeping local value', error);
        });
  }

  /** Flip auto_play exactly like the normal player's menu toggle: optimistic UI,
   * persist to the notification settings API, revert if the call fails. */
  toggleAutoPlay() {
    const next = !this.autoPlayEnabled;
    this.autoPlayEnabled = next;

    this.apiService.callPostAPI('v2/notification/settings',
      { auto_play: next ? 1 : 0 }, environment.notificationApiUrl)
      .subscribe(
        () => {
          localStorage.setItem('autoPlay', JSON.stringify(next));
        },
        error => {
          console.warn('[shorts] auto_play update failed; reverting toggle', error);
          this.autoPlayEnabled = !next;
        });
  }

  // ------------------------------------------------------------------- actions

  /** Heart button. POSTs to the user-actions service like the video detail page
   * does. The endpoint does NOT toggle: status 1 inserts the like row (no-op when it
   * already exists) and status 0 deletes it — so the status has to follow the flip,
   * otherwise an un-like only ever happens in the UI and comes back on reload. */
  toggleLike(item: any) {
    if (!localStorage.getItem('currentUser')) {
      this.userService.redirectUrl(this.router.url);
      this.userService.displayPopup('sign-in');
      return;
    }

    const wasLiked = this.isLiked(item);
    // Optimistic flip — the icon has to answer the tap immediately.
    item.is_like = wasLiked ? 0 : 1;
    if (typeof item.like_count === 'number') {
      item.like_count = Math.max(0, item.like_count + (wasLiked ? -1 : 1));
    }

    this.apiService.callPostAPI('v2/like', {
      video_id: item.slug,
      status: wasLiked ? 0 : 1
    }, environment.userActionApiUrl)
      .subscribe(
        () => {},
        error => {
          item.is_like = wasLiked ? 1 : 0;
          if (typeof item.like_count === 'number') {
            item.like_count = Math.max(0, item.like_count + (wasLiked ? 1 : -1));
          }
          this.apiService.failureResponse(error, false);
        });
  }

  isLiked(item: any): boolean {
    return !!item && (item.is_like === 1 || item.is_like === true);
  }

  /** True while the site-wide share popup (or any other header modal) is up: the feed
   * must not react to keys or wheel behind it. The header flags it on <body>. */
  private get modalOpen(): boolean {
    return document.body.classList.contains('modal-popup');
  }

  /**
   * Share button: opens the site-wide share popup — the one the video page and the
   * shorts cards use — so a short offers the same channels (WhatsApp, Facebook, X,
   * Telegram, mail, copy link, native share) everywhere.
   *
   * The popup builds its link from the video id, which a deep-linked or playlist short
   * may not carry yet; those are completed from watchvideo first. A copy goes to the
   * popup because it writes a placeholder thumbnail onto whatever it is handed.
   */
  share(item: any) {
    if (item.id) {
      this.videoShareService.displayPopup({ ...item });
      return;
    }
    this.apiService.callGetAPI('v2/watchvideo/' + item.slug).subscribe(
      data => {
        if (this.destroyed) return;
        const videos = data && data['response'] && data['response'].videos;
        if (videos) this.adoptDetails(item, videos);
        this.videoShareService.displayPopup({ ...item });
      },
      () => {
        if (!this.destroyed) this.videoShareService.displayPopup({ ...item });
      });
  }

  /** Open the full watch page for the current short, playlist context and all. */
  /** Hand a short the feed cannot decrypt to the normal player, which drives DRM
   * through videojs/EME. Playlist context travels with it, as everywhere else. */
  openInPlayer(item: any) {
    if (!item || !item.slug) return;
    this.router.navigateByUrl(
      this.playQueue.pathFor({ slug: item.slug, is_shorts: 0 }, this.playlistId, this.playlistName));
  }

  openFullVideo(item: any) {
    this.router.navigateByUrl(
      this.playQueue.detailPathFor(item, this.playlistId, this.playlistName));
  }

  goBack() {
    this.location.back();
  }

  onThumbError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.onerror = null;
    img.src = this.defaultPoster;
  }

  /** First frame is up — drop the poster so it can never sit over a playing video. */
  onPlaying(index: number) {
    const item = this.feed[index];
    if (item) item.__started = true;
    if (index === this.activeIndex) this.paused = false;
  }

  trackBySlug(_index: number, item: any) {
    return item.slug;
  }
}
